import { observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { TravelerInformationResponseArgs } from './traveler_information';
import TravelerInformation, { convertTravelerInformationResponseToArgs } from './traveler_information';

export type TicketOprReservationResponseArgs = {
  id: number;
  transport_element_id: number;
  status: string | number; // 'waiting' | 'reserved' | 'reservation_info_gotten' | 'ticket_issued' | 'occupied' | 'failure' | 'ticketing_requested'
  failure_type?: string | number; // 'flight_not_found' | 'insert_error' | 'others'
  retry_count?: number;
  retry_message?: string;
  traveler_information_id?: number;
  ticket_opr_rsv_id?: string;
  ticket_opr_air_company_code?: string;
  ticket_opr_ticket_name?: string;
  ticket_opr_ticket_code?: string;
  ticket_opr_adult_price?: string;
  reserved_at?: string;
  ticket_opr_reservation_number?: string;
  ticket_opr_expired_date?: string;
  ticket_opr_amount?: number;
  reservation_info_gotten_at?: string;
  ticket_opr_confirmation_number?: string;
  ticket_opr_pdf_url?: string;
  ticket_issued_at?: string;
  created_at?: string;
  updated_at?: string;
  error_message?: string;
  notified?: boolean;
  notified_ticket_issued?: boolean;
  s3_file_key?: string;
  traveler_information?: TravelerInformationResponseArgs;
};

export type TicketOprReservationArgs = {
  id: number;
  transportElementId: number;
  status: string | number;
  failureType?: string | number;
  retryCount?: number;
  retryMessage?: string;
  travelerInformationId?: number;
  ticketOprRsvId?: string;
  ticketOprAirCompanyCode?: string;
  ticketOprTicketName?: string;
  ticketOprTicketCode?: string;
  ticketOprAdultPrice?: string;
  reservedAt?: Moment;
  ticketOprReservationNumber?: string;
  ticketOprExpiredDate?: string;
  ticketOprAmount?: number;
  reservationInfoGottenAt?: Moment;
  ticketOprConfirmationNumber?: string;
  ticketOprPdfUrl?: string;
  ticketIssuedAt?: Moment;
  createdAt?: Moment;
  updatedAt?: Moment;
  errorMessage?: string;
  notified?: boolean;
  notifiedTicketIssued?: boolean;
  s3FileKey?: string;
  travelerInformation?: TravelerInformation;
};

export const convertTicketOprReservationResponseToArgs = (
  response: TicketOprReservationResponseArgs
): TicketOprReservationArgs => {
  return {
    id: response.id,
    transportElementId: response.transport_element_id,
    status: response.status,
    failureType: response.failure_type,
    retryCount: response.retry_count,
    retryMessage: response.retry_message,
    travelerInformationId: response.traveler_information_id,
    ticketOprRsvId: response.ticket_opr_rsv_id,
    ticketOprAirCompanyCode: response.ticket_opr_air_company_code,
    ticketOprTicketName: response.ticket_opr_ticket_name,
    ticketOprTicketCode: response.ticket_opr_ticket_code,
    ticketOprAdultPrice: response.ticket_opr_adult_price,
    reservedAt: response.reserved_at ? moment(response.reserved_at) : undefined,
    ticketOprReservationNumber: response.ticket_opr_reservation_number,
    ticketOprExpiredDate: response.ticket_opr_expired_date,
    ticketOprAmount: response.ticket_opr_amount,
    reservationInfoGottenAt: response.reservation_info_gotten_at
      ? moment(response.reservation_info_gotten_at)
      : undefined,
    ticketOprConfirmationNumber: response.ticket_opr_confirmation_number,
    ticketOprPdfUrl: response.ticket_opr_pdf_url,
    ticketIssuedAt: response.ticket_issued_at ? moment(response.ticket_issued_at) : undefined,
    createdAt: response.created_at ? moment(response.created_at) : undefined,
    updatedAt: response.updated_at ? moment(response.updated_at) : undefined,
    errorMessage: response.error_message,
    notified: response.notified,
    notifiedTicketIssued: response.notified_ticket_issued,
    s3FileKey: response.s3_file_key,
    travelerInformation: response.traveler_information
      ? new TravelerInformation(convertTravelerInformationResponseToArgs(response.traveler_information))
      : undefined
  };
};

export default class TicketOprReservation {
  @observable id: number;
  @observable transportElementId: number;
  @observable status: string | number;
  @observable failureType?: string | number;
  @observable retryCount?: number;
  @observable retryMessage?: string;
  @observable travelerInformationId?: number;
  @observable ticketOprRsvId?: string;
  @observable ticketOprAirCompanyCode?: string;
  @observable ticketOprTicketName?: string;
  @observable ticketOprTicketCode?: string;
  @observable ticketOprAdultPrice?: string;
  @observable reservedAt?: Moment;
  @observable ticketOprReservationNumber?: string;
  @observable ticketOprExpiredDate?: string;
  @observable ticketOprAmount?: number;
  @observable reservationInfoGottenAt?: Moment;
  @observable ticketOprConfirmationNumber?: string;
  @observable ticketOprPdfUrl?: string;
  @observable ticketIssuedAt?: Moment;
  @observable createdAt?: Moment;
  @observable updatedAt?: Moment;
  @observable errorMessage?: string;
  @observable notified?: boolean;
  @observable notifiedTicketIssued?: boolean;
  @observable s3FileKey?: string;
  @observable travelerInformation?: TravelerInformation;

  constructor(args: TicketOprReservationArgs) {
    this.id = args.id;
    this.transportElementId = args.transportElementId;
    this.status = args.status;
    this.failureType = args.failureType;
    this.retryCount = args.retryCount;
    this.retryMessage = args.retryMessage;
    this.travelerInformationId = args.travelerInformationId;
    this.ticketOprRsvId = args.ticketOprRsvId;
    this.ticketOprAirCompanyCode = args.ticketOprAirCompanyCode;
    this.ticketOprTicketName = args.ticketOprTicketName;
    this.ticketOprTicketCode = args.ticketOprTicketCode;
    this.ticketOprAdultPrice = args.ticketOprAdultPrice;
    this.reservedAt = args.reservedAt;
    this.ticketOprReservationNumber = args.ticketOprReservationNumber;
    this.ticketOprExpiredDate = args.ticketOprExpiredDate;
    this.ticketOprAmount = args.ticketOprAmount;
    this.reservationInfoGottenAt = args.reservationInfoGottenAt;
    this.ticketOprConfirmationNumber = args.ticketOprConfirmationNumber;
    this.ticketOprPdfUrl = args.ticketOprPdfUrl;
    this.ticketIssuedAt = args.ticketIssuedAt;
    this.createdAt = args.createdAt;
    this.updatedAt = args.updatedAt;
    this.errorMessage = args.errorMessage;
    this.notified = args.notified;
    this.notifiedTicketIssued = args.notifiedTicketIssued;
    this.s3FileKey = args.s3FileKey;
    this.travelerInformation = args.travelerInformation;
  }
}
